import { z } from 'zod';

import { JOB_ROLE_VALUES } from '@discover-frontend/constants';

import translations from '../../../../../../../lang';
import { validateEmail } from '../../../../../../../utils';

import { PASSWORD_REGEX } from '../constants';

const { TRIAL_SIGNUP } = translations['en-US'];
const { ERRORS } = TRIAL_SIGNUP;

/**
 * A bare `z.enum` aborts the whole object parse on a missing value, which makes
 * Zod skip the cross-field `superRefine` below. Basing the check on `z.string`
 * keeps the failure "dirty" so the password comparison still runs.
 */
const requiredOption = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .string()
    .refine(
      (value): value is T[number] =>
        (values as readonly string[]).includes(value),
      { message: ERRORS.REQUIRED }
    );

const isStrongPassword = (password: string): boolean =>
  PASSWORD_REGEX.lowercase.test(password) &&
  PASSWORD_REGEX.uppercase.test(password) &&
  PASSWORD_REGEX.number.test(password) &&
  PASSWORD_REGEX.minLength.test(password) &&
  PASSWORD_REGEX.special.test(password);

export const trialSignupSchema = z
  .object({
    companyName: z.string().trim().min(1, ERRORS.REQUIRED),
    firstName: z.string().trim().min(1, ERRORS.REQUIRED),
    lastName: z.string().trim().min(1, ERRORS.REQUIRED),
    jobRole: requiredOption(JOB_ROLE_VALUES),
    countryCode: z.string().trim().min(1, ERRORS.REQUIRED),
    // Corporate-domain / availability checks are owned by the backend API.
    email: z
      .string()
      .trim()
      .min(1, ERRORS.REQUIRED)
      .refine(validateEmail, { message: ERRORS.EMAIL_FORMAT }),
    targetPanel: z.string().trim().min(1, ERRORS.REQUIRED),
    password: z
      .string()
      .min(1, ERRORS.REQUIRED)
      .refine(isStrongPassword, { message: ERRORS.PASSWORD_WEAK }),
    confirmPassword: z.string().min(1, ERRORS.REQUIRED),
    acceptedTerms: z.boolean().refine((value) => value === true, {
      message: ERRORS.TERMS_REQUIRED,
    }),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: ERRORS.PASSWORD_MISMATCH,
        path: ['confirmPassword'],
      });
    }
  });

export type TrialSignupFormValues = z.infer<typeof trialSignupSchema>;

export type TrialInviteCredentials = {
  id: string;
  verification_code: string;
};

/** Accounts API body for POST /companies/trial/register */
export type TrialRegisterApiPayload = {
  id: string;
  verification_code: string;
  company_name: string;
  first_name: string;
  last_name: string;
  job_function: string;
  country_code: string;
  email: string;
  selected_panel: string;
  password: string;
  verified_password: string;
};

export const toTrialRegisterPayload = (
  values: TrialSignupFormValues,
  invite: TrialInviteCredentials
): TrialRegisterApiPayload => ({
  id: invite.id,
  verification_code: invite.verification_code,
  company_name: values.companyName,
  first_name: values.firstName,
  last_name: values.lastName,
  job_function: values.jobRole,
  country_code: values.countryCode,
  email: values.email,
  selected_panel: values.targetPanel,
  password: values.password,
  verified_password: values.confirmPassword,
});
